import React, { useState } from 'react';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Button, MenuItem, Stack, TextField } from '@mui/material';

import { type AiCodeKind } from '~/services/PromoVoucherAiService';

import PromoFeature from './PromoFeature';

export type AiWorkflowAction = 'create' | 'update' | 'create-or-update';

type Props = {
  kind: AiCodeKind;
  onContinue: (name: string, action: AiWorkflowAction, query: string) => void | Promise<void>;
  isPending?: boolean;
};

export default function PromoAiWorkflowLauncher({ kind, onContinue, isPending = false }: Props) {
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [action, setAction] = useState<AiWorkflowAction>('create-or-update');
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <PromoFeature featureKey="ai-promo-tools-access" presentation="accordion">
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="flex-start" spacing={2}>
          <TextField
            label="Query"
            fullWidth
            multiline
            minRows={2}
            value={query}
            onFocus={() => setIsExpanded(true)}
            onChange={(event) => setQuery(event.target.value)}
            disabled={isPending}
            sx={{ flex: 1 }}
          />
          <TextField
            label={kind === 'promo' ? 'Promo code name' : 'Voucher name'}
            fullWidth
            value={name}
            onFocus={() => setIsExpanded(true)}
            onChange={(event) => setName(event.target.value)}
            disabled={isPending}
            sx={{ width: { xs: '100%', sm: '30%' }, flexShrink: 0 }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && name.trim() && query.trim() && !isPending) {
                event.preventDefault();
                void onContinue(name.trim(), action, query.trim());
              }
            }}
          />
        </Stack>
        {isExpanded && (
          <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
            <TextField
              select
              label="Action"
              value={action}
              disabled={isPending}
              onChange={(event) => setAction(event.target.value as AiWorkflowAction)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="create">Create</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="create-or-update">Create or update</MenuItem>
            </TextField>
            <Button
              variant="contained"
              startIcon={<AutoFixHighIcon />}
              disabled={!name.trim() || !query.trim() || isPending}
              onClick={() => void onContinue(name.trim(), action, query.trim())}
            >
              {isPending ? 'Finding...' : 'Continue with AI'}
            </Button>
          </Stack>
        )}
      </Stack>
    </PromoFeature>
  );
}
